import { GameDirective } from "./GameDirective.js";
import type { PlayerStateController } from "./playerstate/PlayerStateController.js";
import type { DiceController } from "./dice/DiceController.js";
import type { PlayerStateDirective } from "./playerstate/PlayerStateDirective.js";
import type { DiceDirective } from "./dice/DiceDirective.js";
import { RoyalUrRoll } from "../royalur/RoyalUrRoll.js";
import type { BoardController } from "./board/BoardController.js";
import type { GameState } from "../../../royalur/engine/state/GameState.js";
import type { BoardDirective } from "./board/BoardDirective.js";
import { WaitingForRollGameState } from "../../../royalur/engine/state/WaitingForRollGameState.js";
import { RolledGameState } from "../../../royalur/engine/state/RolledGameState.js";
import { WaitingForMoveGameState } from "../../../royalur/engine/state/WaitingForMoveGameState.js";
import { MovedGameState } from "../../../royalur/engine/state/MovedGameState.js";
import { EndGameState } from "../../../royalur/engine/state/EndGameState.js";
import type { GameSource } from "./source/GameSource.js";
import type { Game } from "../../../royalur/Game.js";
import { ActionGameState } from "../../../royalur/engine/state/ActionGameState.js";
import { GameUpdateEvent } from "./source/GameUpdateEvent.js";
import { GameDiff } from "../../../royalur/GameDiff.js";
import { ListenerStore } from "../../ListenerStore.js";
import type { ReactionController } from "./reactions/ReactionController.js";
import { PlayerType } from "../../../royalur/model/PlayerType.js";
import type { ReactionType } from "../ReactionType.js";
import { PlayerState } from "../../../royalur/model/PlayerState.js";
import { GameController } from "./GameController.js";
import type { GameThemeType } from "../theme/GameThemeType.js";


export type WinListener = () => void;


/**
 * Coordinates the animations and interactions within a game.
 */
export class PlayGameController extends GameController {
    private readonly skipDiceRolls: boolean;
    private readonly winListeners: ListenerStore<WinListener>;

    private displayedGame: Game;
    private stateIndex: number = 0;

    constructor(
        playerStateController: PlayerStateController,
        reactionController: ReactionController,
        diceController: DiceController,
        boardController: BoardController,
        source: GameSource,
        defaultThemeType: GameThemeType,
        skipDiceRolls: boolean,
    ) {
        super(
            playerStateController,
            reactionController,
            diceController,
            boardController,
            source,
            defaultThemeType,
        );
        this.skipDiceRolls = skipDiceRolls;
        this.winListeners = new ListenerStore<WinListener>();

        this.displayedGame = source.game.get();
        this.updateFromGame(this.displayedGame, new GameUpdateEvent(true));

        boardController.setMusicEnabled(true);
        this.postConstructor();
    }

    override setup(): () => void {
        const cleanup: (() => void)[] = [
            super.setup(),
        ];

        cleanup.push(this.source.game.subscribe(
            (game, event) => this.updateFromGame(game, event),
        ));
        cleanup.push(this.source.addAnimateDiceRollingListener(
            () => this.handleAnimateDiceRolling(),
        ));

        cleanup.push(this.source.addReactionListener(
            (player, reaction) => this.handleReaction(player, reaction),
        ));
        cleanup.push(this.reactionController.addReactionListener(
            (player, reaction) => this.source.handleReaction(player, reaction),
        ));

        cleanup.push(this.diceController.addRollListener(
            event => this.source.handleRoll(event),
        ));

        cleanup.push(this.boardController.addMoveListener(
            event => this.source.handleMove(event),
        ));

        // There may be AI moves to make.
        this.updateFromGame(this.displayedGame, new GameUpdateEvent(false));

        return () => cleanup.forEach(fn => fn());
    }

    override addWinListener(listener: WinListener): () => void {
        return this.winListeners.add(listener);
    }

    override rematch() {
        this.source.handleRematch();
    }

    override cancelRematch() {
        this.source.cancelRematch();
    }

    override handleReaction(player: PlayerType | null, reaction: ReactionType) {
        this.reactionController.pushDirectives(
            this.reactionController.createReaction(
                player, reaction,
            ),
        );
    }

    override handleAnimateDiceRolling() {
        this.pushDirective(this.generateDiceRollingGameDirective());
    }

    private resolveNewGame(newGame: Game) {
        if (newGame === this.displayedGame)
            return;

        const diff = GameDiff.create(newGame, this.displayedGame);
        this.displayedGame = newGame;

        if (diff.hasCommonIndex()) {
            this.stateIndex = Math.min(
                diff.getCommonIndexNew() + 1,
                diff.getNewStates().length - 1,
            );
        } else {
            // Start of the game.
            if (!diff.doesPreviousContainAction()) {
                this.stateIndex = 0;
            } else {
                this.stateIndex = newGame.getStates().length - 1;
            }
        }
    }

    private updateFromGame(newGame: Game, event: GameUpdateEvent) {
        const skipAnimation = event.shouldSkipAnimation();

        // Update to the new game.
        const gameChanged = (this.displayedGame !== newGame);
        if (gameChanged) {
            this.resolveNewGame(newGame);
        }

        // Add the new states as directives.
        const states = this.displayedGame.getStates();
        const newDirectives: GameDirective[] = [];
        while (this.stateIndex < states.length) {
            const index = this.stateIndex;
            const state = states[index];
            const nextState = (index + 1 >= states.length ? null : states[index + 1]);
            this.stateIndex += 1;
            if (skipAnimation && state instanceof ActionGameState)
                continue;
            if (this.skipDiceRolls && state instanceof RolledGameState)
                continue;

            newDirectives.push(this.generateGameDirective(state, nextState));
        }
        this.pushDirectives(newDirectives);

        // Call the win listeners.
        if (newGame.isFinished()) {
            this.winListeners.invoke();
        }
    }

    generateGameDirective(
        state: GameState,
        nextState: GameState | null = null,
    ): GameDirective {
        // This is not correct, but it doesn't really matter when playing a game.
        const moveIndex = this.getGame().get().getActionStates().length;

        const boardDirectives: BoardDirective[] = [];
        const diceDirectives: DiceDirective[] = [];
        const playerStateDirectives: PlayerStateDirective[] = [];

        // Create the dice directives.
        if (state instanceof WaitingForRollGameState) {
            const canInteract = this.source.isLocalHumanPlayer(state.getTurn());
            if (canInteract) {
                diceDirectives.push(...this.diceController.createWaitForRoll(
                    state.getTurn(),
                ));
            } else {
                diceDirectives.push(...this.diceController.createWait(
                    state.getTurn(),
                ));
            }
        } else if (state instanceof RolledGameState) {
            const settings = this.source.getRules().getSettings();
            diceDirectives.push(...this.diceController.createRoll(
                state.getTurn(),
                RoyalUrRoll.cast(state.getRoll()),
                state.getAvailableMoves().length === 0,
                state.getTurnPlayer().getScore() >= settings.getStartingPieceCount() - 1,
            ));
        } else {
            let roll: RoyalUrRoll | null = null;
            if (state instanceof WaitingForMoveGameState || state instanceof MovedGameState) {
                roll = RoyalUrRoll.cast(state.getRoll());
            } else {
                // Get the last dice roll from the game.
                const states = this.displayedGame.getStates();
                for (let index = this.stateIndex - 1; index >= 0; --index) {
                    const previousState = states[index];
                    if (previousState instanceof WaitingForMoveGameState
                        || previousState instanceof MovedGameState
                        || previousState instanceof RolledGameState) {
                        roll = RoyalUrRoll.cast(previousState.getRoll());
                        break;
                    }
                }
            }
            if (roll === null) {
                roll = RoyalUrRoll.createZeroed(4);
            }
            diceDirectives.push(...this.diceController.createRolled(
                state.getSubject() ?? PlayerType.LIGHT, roll,
            ));
        }

        // Create the board directives.
        if (state instanceof WaitingForMoveGameState) {
            const canInteract = this.source.isLocalHumanPlayer(state.getTurn());
            if (canInteract) {
                boardDirectives.push(...this.boardController.createPromptForMove(
                    this.source.getRules(),
                    moveIndex,
                    state,
                    RoyalUrRoll.cast(state.getRoll()),
                    state.getAvailableMoves(),
                ));
            } else {
                boardDirectives.push(...this.boardController.createWait(
                    this.source.getRules(),
                    moveIndex,
                    state,
                    state.getSubject(),
                    state.isFinished(),
                ));
            }
        } else if (state instanceof MovedGameState) {
            boardDirectives.push(...this.boardController.createMakeMove(
                this.source.getRules(),
                moveIndex,
                state,
                state.getMove(),
                (nextState !== null && nextState instanceof EndGameState),
            ));

            // We want pieces to subtract immediately, but we don't want
            // pieces to increase until after the move finishes.
            const piecesSource = nextState ?? state;
            playerStateDirectives.push(...this.playerStateController.createBasic(
                moveIndex,
                new PlayerState(
                    PlayerType.LIGHT,
                    Math.min(
                        state.getLightPlayer().getPieceCount(),
                        piecesSource.getLightPlayer().getPieceCount(),
                    ),
                    state.getLightPlayer().getScore(),
                ),
                new PlayerState(
                    PlayerType.DARK,
                    Math.min(
                        state.getDarkPlayer().getPieceCount(),
                        piecesSource.getDarkPlayer().getPieceCount(),
                    ),
                    state.getDarkPlayer().getScore(),
                ),
                state.getSubject(),
            ));

        } else {
            boardDirectives.push(...this.boardController.createWait(
                this.source.getRules(),
                moveIndex,
                state,
                state.getSubject() ?? PlayerType.LIGHT,
                state.isFinished(),
            ));
        }

        // Create the player state directives.
        if (playerStateDirectives.length === 0) {
            playerStateDirectives.push(...this.playerStateController.createBasic(
                moveIndex,
                state.getLightPlayer(),
                state.getDarkPlayer(),
                state.getSubject() ?? PlayerType.LIGHT,
            ));
        }

        // Create the directive!
        return new GameDirective(
            this.assignNextDirectiveID(),
            state,
            boardDirectives,
            diceDirectives,
            playerStateDirectives,
        );
    }

    generateDiceRollingGameDirective(): GameDirective {
        const state = this.source.game.get().getState();
        const template = this.generateGameDirective(state);
        const diceDirectives = this.diceController.createRoll(
            state.getSubject() ?? PlayerType.LIGHT,
            null, false, false,
        );
        return new GameDirective(
            this.assignNextDirectiveID(),
            state,
            template.boardDirectives,
            diceDirectives,
            template.playerStateDirectives,
        );
    }
}
